import React, { useState, useEffect, useRef, useCallback } from "react";
import { Formik, Form } from "formik";
import { useNavigate } from 'react-router-dom';
import moment from "moment";
import { useDispatch } from 'react-redux';
import TooltipMaterial from "@material-ui/core/Tooltip";

import AlertModal from "components/AlertModal";
import Select from "components/FormElements/Select";
import CheckboxInput from "components/FormElements/CheckboxInput";
import TextInput from "components/FormElements/TextInput";
import TextArea from "components/FormElements/TextArea";
import DatePicker from "components/FormElements/DatePicker";
import TimePickerInput from "components/FormElements/TimePickerInput";
import NumberInput from "components/FormElements/NumberInput";
import ConditionDropdown from "components/FormElements/ConditionDropdown";
import MultiSelectCheckboxDropdown from "components/FormElements/MultiSelectCheckboxDropdown";
import Button from "components/Button";
import UploadDummy from "assets/images/UploadDummy.png";
import UploadSpecIcon from "assets/svg/UploadSpecIcon";

import {
  uploadSpectFile,
  createOrder,
  updateOrder,
  orderRefNumber,
  locationList
} from "redux/actions/requestOrders";

import {
  Container,
  Wrapper,
  Footer,
  FormWrapper,
  InputFieldsWrapper,
  MixHeaderGrid,
  PalletRowContainer,
  PalletRowGrid,
  RowActionGroup,
  RowActionButton,
  RowUploadFileBadge,
  AddMixPalletBtn,
  CloneOuterWrapper
} from "./OrderStyled";

import {
  getPalletSpecificDetails
} from "./requestOrdersUtils";

import { useText } from "locales/useGetText";

import {
  palletList,
  comboPalletList,
  materialOptions
} from "./constants";

const MixPalletRequestOrder = ({
  palletSpecsConfig,
  onToggleMixLoad,
  isModifyForm = false,
  modifiedOrderRefNo = "",
  editResponse = null
}) => {
  const locale = useText("requestOrder");
  const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserData'));
  const token = loggedInUserInfo?.token;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [activeUploadRowIndex, setActiveUploadRowIndex] = useState(null);
  const [loader, setLoader] = useState(false);

  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalLink, setModalLink] = useState("");
  const toggleModal = () => setModal(!modal);

  const showValidationError = (msg) => {
    setModalTitle("");
    setModalContent(msg);
    setModalLink("");
    toggleModal();
  };

  const equipmentList = ["Flatbed", "Enclosed Trailer", "Straight Truck"];

  // Header State
  const [locationListResponse, setLocationListResponse] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLocationAddress, setSelectedLocationAddress] = useState(null);

  const [requestDeliveryDate, setRequestDeliveryDate] = useState(
    moment().add(4, 'days').format('YYYY-MM-DD')
  );
  const [apptFrom, setApptFrom] = useState("00:00");
  const [apptTo, setApptTo] = useState("23:00");

  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [equipmentState, setEquipmentState] = useState({
    equipment0: false,
    equipment1: false,
    equipment2: false,
  });

  const [poNumber, setPoNumber] = useState("");
  const [remark, setRemark] = useState("");
  const [isCloningOrder, setIsCloningOrder] = useState(false);
  const [clonedOrderRefNo, setClonedOrderRefNo] = useState("");
  const formikRef = useRef(null);

  // Pallet Rows State
  const createDefaultRow = () => ({
    id: Date.now() + Math.random(),
    material: "WOOD",
    condition: "",
    newConditionType: "",
    palletType: "",
    moisture: "",
    size: "",
    grade: "",
    quantity: 540,
    maxQuantity: 540,
    lots: 1,
    isHeatTreated: false,
    length: "",
    width: "",
    height: "",
    uploadedFiles: [],
    apiSpecFiles: [],
    isClonedOrEdited: false
  });

  const [palletRows, setPalletRows] = useState([createDefaultRow()]);

  // Fetch Locations on Mount
  useEffect(() => {
    fetchUserLocations();
  }, []);

  const fetchUserLocations = useCallback(async () => {
    if (!token) return;
    const response = await dispatch(
      locationList(loggedInUserInfo?.locationType, loggedInUserInfo?.customerId, token)
    );
    if (response?.data?.success) {
      const arr = [];
      const locations = response.data.data.locations || [];
      for (let i = 0; i < locations.length; i++) {
        const item = locations[i];
        if (item.mainLocation) {
          const address = item.mainLocation.address || {};
          const label = `${item.mainLocation.locationName || ''}, ${address.street || ''}, ${address.city || ''}, ${address.state || ''}, ${address.zipCode || ''}`;
          arr.push({
            label,
            value: item.mainLocation.locationId,
            response: address
          });
        }
        if (item.subLocations) {
          for (let j = 0; j < item.subLocations.length; j++) {
            const sub = item.subLocations[j];
            const address = sub.address || {};
            const label = `${address.name || ''}, ${address.street || ''}, ${address.city || ''}, ${address.state || ''}, ${address.zipCode || ''}`;
            arr.push({
              label,
              value: sub.locationId,
              response: address
            });
          }
        }
      }
      setLocationListResponse(arr);
    }
  }, [token, loggedInUserInfo, dispatch]);

  const handleLocationChange = (val) => {
    setSelectedLocationId(val);
    const match = locationListResponse.find(loc => loc.value === val);
    if (match) setSelectedLocationAddress(match.response);
  };

  // Synchronize location address when locationListResponse loads after order populating
  useEffect(() => {
    if (selectedLocationId && locationListResponse.length > 0 && !selectedLocationAddress) {
      const match = locationListResponse.find(loc => loc.value === selectedLocationId);
      if (match) setSelectedLocationAddress(match.response);
    }
  }, [selectedLocationId, locationListResponse, selectedLocationAddress]);

  // Row Manipulation
  const handleAddRow = () => {
    setPalletRows(prev => [...prev, createDefaultRow()]);
  };

  const handleRemoveRow = (index) => {
    if (palletRows.length === 1) return;
    setPalletRows(prev => prev.filter((_, idx) => idx !== index));
  };

  const updateRowField = (index, field, value) => {
    setPalletRows(prev => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row[field] = value;

      const combinationFields = ["material", "condition", "newConditionType", "palletType", "moisture", "size", "grade"];
      if (combinationFields.includes(field)) {
        row.apiSpecFiles = [];
        row.uploadedFiles = [];
        row.isClonedOrEdited = false;
      }

      if (field === "material") {
        row.condition = "";
        row.newConditionType = "";
        row.palletType = "";
        row.moisture = "";
        row.size = "";
        row.grade = "";
        row.length = "";
        row.width = "";
        row.height = "";
        if (value === "PLASTIC") {
          row.quantity = 786;
          row.maxQuantity = 786;
        } else {
          row.quantity = 540;
          row.maxQuantity = 540;
        }
      } else if (field === "condition") {
        row.palletType = "";
        row.moisture = "";
        row.size = "";
        row.grade = "";
      } else if (field === "palletType") {
        row.moisture = "";
        row.size = "";
        row.grade = "";
      } else if (field === "moisture") {
        row.size = "";
        row.grade = "";
      } else if (field === "size") {
        row.grade = "";
      }

      if (field === "isHeatTreated") {
        if (formikRef.current) {
          formikRef.current.setFieldValue(`isHeatTreated_${index}`, Boolean(value));
        }
      }

      const activeConds = row.condition === "RECYCLED" ? ["recycled"] : row.condition === "NEW" ? ["newCondition"] : row.condition === "COMBO" ? ["combo"] : [];
      const details = getPalletSpecificDetails(palletSpecsConfig, row.material, activeConds, row.palletType);

      let sizeDetails = details;
      if (row.moisture && details.some(d => d.moisture === row.moisture)) {
        sizeDetails = details.filter(d => d.moisture === row.moisture);
      }

      const gradeDetails = sizeDetails.filter(d => d.size === row.size);
      const match = gradeDetails.find(d => d.size === row.size && (!row.grade || d.grade === row.grade));
      const matchingSize = sizeDetails.find(d => d.size === row.size);
      const specQty = match?.quantity || matchingSize?.quantity || (row.material === "PLASTIC" ? 786 : 540);

      row.maxQuantity = specQty;
      if (field !== "quantity" && field !== "lots" && field !== "isHeatTreated") {
        row.quantity = specQty;
      }

      updated[index] = row;
      return updated;
    });
  };

  // Cascading options generator per row
  const getRowCascadingOptions = (row) => {
    const activeConds = row.condition === "RECYCLED" ? ["recycled"] : row.condition === "NEW" ? ["newCondition"] : row.condition === "COMBO" ? ["combo"] : [];
    const details = getPalletSpecificDetails(palletSpecsConfig, row.material, activeConds, row.palletType);

    const uniqueMoistures = [...new Set(details.map(d => d.moisture).filter(Boolean))];
    const moistureOptions = uniqueMoistures.map(m => ({ label: m, value: m }));

    let sizeDetails = details;
    if (uniqueMoistures.length > 0 && row.moisture) {
      sizeDetails = details.filter(d => d.moisture === row.moisture);
    }
    const uniqueSizes = [...new Set(sizeDetails.map(d => d.size).filter(Boolean))];
    const rawSizeOpts = uniqueSizes.map(s => {
      const matching = sizeDetails.find(d => d.size === s);
      return { label: s, value: s, quantity: matching?.quantity };
    });

    const pTypeLower = row.palletType?.toLowerCase() || "";
    const isEuroPallet = pTypeLower === "europallet" || pTypeLower === "euro";

    const sizeOptions = row.palletType === "custom"
      ? [{ label: "Custom", value: "Custom" }]
      : isEuroPallet
        ? rawSizeOpts
        : (rawSizeOpts.some(opt => opt.value === "Custom") ? rawSizeOpts : [...rawSizeOpts, { label: "Custom", value: "Custom" }]);

    const gradeDetails = sizeDetails.filter(d => d.size === row.size);
    const uniqueGrades = [...new Set(gradeDetails.map(d => d.grade).filter(Boolean))];
    const gradeOptions = uniqueGrades.map(g => ({ label: g, value: g }));

    const match = gradeDetails.find(d => d.size === row.size && (!row.grade || d.grade === row.grade));
    const matchingSize = sizeDetails.find(d => d.size === row.size);
    const specQty = match?.quantity || matchingSize?.quantity || (row.material === "PLASTIC" ? 786 : 540);
    const defaultSpecs = match?.file && match.file !== "Custom Spec Upload"
      ? [{ orignalFileName: match.file, fileName: match.file, label: match.file, value: match.file }]
      : [];

    return { moistureOptions, sizeOptions, gradeOptions, defaultSpecs, specQty };
  };

  // File Upload Per Row
  const triggerRowFileUpload = (index) => {
    setActiveUploadRowIndex(index);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (activeUploadRowIndex !== null && selectedFiles.length > 0) {
      const updated = [...palletRows];
      const existing = updated[activeUploadRowIndex].uploadedFiles || [];
      const combined = [...existing, ...selectedFiles];

      if (combined.length > 3) {
        showValidationError("Maximum 3 files can be uploaded per row.");
        return;
      }

      const totalSize = combined.reduce((acc, f) => acc + (f.size || 0), 0);
      if (totalSize > 5 * 1024 * 1024) {
        showValidationError("Uploaded files should not exceed 5 MB total.");
        return;
      }

      updated[activeUploadRowIndex].uploadedFiles = combined;
      setPalletRows(updated);
    }
    e.target.value = "";
  };

  const handleRemoveRowFile = (rowIndex, fileIndex) => {
    const updated = [...palletRows];
    updated[rowIndex].uploadedFiles = updated[rowIndex].uploadedFiles.filter((_, fIdx) => fIdx !== fileIndex);
    setPalletRows(updated);
  };

  const handleRemoveApiSpecFile = (rowIndex, fileIndex) => {
    setPalletRows(prev => {
      const updated = [...prev];
      const row = { ...updated[rowIndex] };
      row.apiSpecFiles = (row.apiSpecFiles || []).filter((_, idx) => idx !== fileIndex);
      updated[rowIndex] = row;
      return updated;
    });
  };

  // Form Populating Function for Edit & Clone
  const populateFormFromOrder = useCallback((orderData, isEditMode = false) => {
    if (!orderData) return;

    // Location
    const locId = orderData.location?.locationId || orderData.locationId || "";
    if (locId) {
      setSelectedLocationId(locId);
      const match = locationListResponse.find(loc => loc.value === locId);
      if (match) {
        setSelectedLocationAddress(match.response);
      } else if (orderData.shippingAddress || orderData.location?.shippingAddress) {
        setSelectedLocationAddress(orderData.shippingAddress || orderData.location?.shippingAddress);
      }
    }

    // Equipment
    const eqObj = { equipment0: false, equipment1: false, equipment2: false };
    const eqArr = [];
    if (orderData.equipmentRequirements) {
      orderData.equipmentRequirements.forEach(eq => {
        if (eq === "FLAT_BED") { eqObj.equipment0 = true; eqArr.push("equipment0"); }
        if (eq === "ENCLOSED_TRAILER") { eqObj.equipment1 = true; eqArr.push("equipment1"); }
        if (eq === "STRAIGHT_TRUCK") { eqObj.equipment2 = true; eqArr.push("equipment2"); }
      });
    }
    setEquipmentState(eqObj);
    setSelectedEquipment(eqArr);

    // Delivery Date & Appt Window
    const ld = Array.isArray(orderData.loadDetails) ? orderData.loadDetails[0] : orderData.loadDetails;
    if (ld?.requestDeliveryDate) {
      const clonedDate = moment(ld.requestDeliveryDate);
      const today = moment().startOf('day');
      const validDate = (isEditMode || clonedDate.isAfter(today)) ? clonedDate : moment().add(1, 'day');
      setRequestDeliveryDate(validDate.format("YYYY-MM-DD"));
    }

    const fromTime = ld?.deliveryFromtime || ld?.deliveryFromTime || ld?.appt || ld?.deliveryWindowBegin || "00:00";
    const toTime = ld?.deliveryTotime || ld?.deliveryToTime || ld?.apptTo || ld?.deliveryWindowEnd || "23:00";
    setApptFrom(fromTime);
    setApptTo(toTime);

    // PO & Remark
    const clonedPo = ld?.poNumber || orderData.poNumber || "";
    setPoNumber(clonedPo);
    const clonedRemark = orderData.remark || orderData.comments || orderData.remarkText || "";
    setRemark(clonedRemark);

    if (!isEditMode) {
      const refNo = orderData.orderRefNumber || orderData.data?.orderRefNumber || "";
      if (refNo) setClonedOrderRefNo(refNo);
    }

    // Spec files fallback
    const globalApiSpecs = (orderData.specificationFiles || []).map(f => ({
      orignalFileName: f.orignalFileName || f.fileName || f.orignalFilename || f.name || "",
      fileName: f.fileName || f.orignalFileName || f.name || ""
    }));

    // Combinations / Details -> Rows
    let rawCombinations = [];
    if (
      orderData.mixPalletDetails ||
      (orderData.palletCombinations && Array.isArray(orderData.palletCombinations) && orderData.palletCombinations.length > 0)
    ) {
      rawCombinations = orderData.palletCombinations || [];
    } else if (orderData.palletDetails) {
      const pd = orderData.palletDetails;
      let pType = pd.palletType?.toLowerCase() || "";
      if (pType === "euro" || pd.palletType === "EUROPALLET") pType = "euroPallet";
      if (pType === "stringer") pType = "stringer";
      if (pType === "block") pType = "block";
      if (pType === "custom") pType = "custom";

      const { specQty } = getRowCascadingOptions({
        material: pd.material || "WOOD",
        condition: Array.isArray(pd.condition) ? (pd.condition[0] || "") : (pd.condition || ""),
        palletType: pType || "",
        moisture: pd.moisture || "",
        size: pd.size || "",
        grade: pd.grade || ""
      });
      const defaultMaterialMax = pd.material === "PLASTIC" ? 786 : 540;
      const qtyDetails = ld?.quantityDetails;
      rawCombinations = [{
        palletDetails: pd,
        quantity: qtyDetails?.quantity || ld?.quantity || 540,
        originalQuantity: specQty || defaultMaterialMax
      }];
    }

    const newRows = rawCombinations.map((combo, index) => {
      const pd = combo.palletDetails || {};
      let pType = pd.palletType?.toLowerCase() || "";
      if (pType === "euro" || pd.palletType === "EUROPALLET") pType = "euroPallet";
      if (pType === "stringer") pType = "stringer";
      if (pType === "block") pType = "block";
      if (pType === "custom") pType = "custom";

      let newCond = "";
      if (pd.newConditionType === 'HARDWOOD') newCond = 'HARDWOOD';
      else if (pd.newConditionType === 'SOFTWOOD') newCond = 'SOFTWOOD';

      const { specQty } = getRowCascadingOptions({
        material: pd.material || "WOOD",
        condition: Array.isArray(pd.condition) ? (pd.condition[0] || "") : (pd.condition || ""),
        palletType: pType || "",
        moisture: pd.moisture || "",
        size: pd.size || "",
        grade: pd.grade || ""
      });
      const defaultMaterialMax = pd.material === "PLASTIC" ? 786 : 540;
      const qtyDetails = ld?.quantityDetails;
      const clonedQty = combo.quantity || qtyDetails?.quantity || 540;
      const clonedMaxQty = specQty || defaultMaterialMax;

      const heatTreatedVal = pd.heatTreared !== undefined ? pd.heatTreared : (pd.heatTreated !== undefined ? pd.heatTreated : (pd.isHeatTreated !== undefined ? pd.isHeatTreated : false));

      const rowSpecsList = (combo.specificationFiles && combo.specificationFiles.length > 0)
        ? combo.specificationFiles
        : (pd.specificationFiles && pd.specificationFiles.length > 0)
          ? pd.specificationFiles
          : globalApiSpecs;

      const rowApiSpecs = rowSpecsList.map(f => ({
        orignalFileName: f.orignalFileName || f.fileName || f.orignalFilename || f.name || "",
        fileName: f.fileName || f.orignalFileName || f.name || ""
      }));

      return {
        id: Date.now() + Math.random() + index,
        material: pd.material || "WOOD",
        condition: Array.isArray(pd.condition) ? (pd.condition[0] || "") : (pd.condition || ""),
        newConditionType: newCond,
        palletType: pType || "",
        moisture: pd.moisture || "",
        size: pd.size || "",
        grade: pd.grade || "",
        quantity: clonedQty,
        lots: qtyDetails?.lots || 1,
        maxQuantity: clonedMaxQty,
        isHeatTreated: Boolean(heatTreatedVal),
        length: pd.length || "",
        width: pd.width || "",
        height: pd.height || "",
        uploadedFiles: [],
        apiSpecFiles: rowApiSpecs,
        isClonedOrEdited: true
      };
    });

    if (newRows.length > 0) {
      setPalletRows(newRows);
      newRows.forEach((r, idx) => {
        if (formikRef.current) {
          formikRef.current.setFieldValue(`isHeatTreated_${idx}`, Boolean(r.isHeatTreated));
        }
      });
    }
  }, [locationListResponse]);

  const hasLoadedEditRef = useRef(null);

  useEffect(() => {
    if (isModifyForm && editResponse && hasLoadedEditRef.current !== modifiedOrderRefNo) {
      hasLoadedEditRef.current = modifiedOrderRefNo;
      const orderData = editResponse?.data?.data || editResponse?.data || editResponse;
      populateFormFromOrder(orderData, true);
    }
  }, [isModifyForm, editResponse, modifiedOrderRefNo, populateFormFromOrder]);

  // Load Order Details (Clone)
  const loadOrderDetailsAPI = async (refNumber, setFieldValue) => {
    if (!refNumber) return;
    setLoader(true);
    setClonedOrderRefNo(refNumber);
    if (setFieldValue) setFieldValue("orderId", refNumber);
    if (formikRef.current) formikRef.current.setFieldValue("orderId", refNumber);

    const response = await dispatch(orderRefNumber(refNumber, token));
    if (response?.data?.success && (response?.data?.data || response?.data)) {
      const orderData = response.data.data || response.data;
      const isMixOrder = Boolean(
        orderData?.mixPalletDetails === true ||
        (orderData?.palletCombinations && Array.isArray(orderData.palletCombinations) && orderData.palletCombinations.length > 0)
      );

      if (!isMixOrder) {
        setModalContent(locale('singlePalletCloneError') || "Single pallet order cannot be cloned in mix pallet page.");
        setModalTitle('');
        setModalLink("");
        toggleModal();
        setLoader(false);
        return;
      }

      populateFormFromOrder(orderData, false);
    } else {
      setModalContent(response?.data?.error?.message || response?.message || locale('genericErrorLater'));
      setModalLink("");
      toggleModal();
    }
    setLoader(false);
  };

  const handleMixSubmit = async (values = {}) => {
    const activePoNumber = values?.poNumber !== undefined ? values.poNumber : (poNumber || "");
    const activeRemark = values?.remark !== undefined ? values.remark : (remark || "");

    if (!selectedLocationId) {
      showValidationError(locale('locationNameRequired'));
      return;
    }

    const equipmentRequirements = [];
    if (equipmentState.equipment0) equipmentRequirements.push("FLAT_BED");
    if (equipmentState.equipment1) equipmentRequirements.push("ENCLOSED_TRAILER");
    if (equipmentState.equipment2) equipmentRequirements.push("STRAIGHT_TRUCK");

    if (equipmentRequirements.length === 0) {
      showValidationError(locale('equipmentRequired'));
      return;
    }

    const apptFromStr = typeof apptFrom === "string" ? apptFrom : (apptFrom?.target?.value || "00:00");
    const apptToStr = typeof apptTo === "string" ? apptTo : (apptTo?.target?.value || "23:00");

    const fromTimeVal = parseInt((apptFromStr || "00:00").replace(":", "") || "0");
    const toTimeVal = parseInt((apptToStr || "23:00").replace(":", "") || "2300");
    if (toTimeVal < fromTimeVal) {
      showValidationError(locale('appointmentWindowInvalid'));
      return;
    }

    // Validate Rows
    for (let i = 0; i < palletRows.length; i++) {
      const row = palletRows[i];
      const rowNum = i + 1;
      const { moistureOptions, gradeOptions, defaultSpecs } = getRowCascadingOptions(row);
      const showMoisture = row.material === "WOOD" && moistureOptions && moistureOptions.length > 0;
      const showGrade = row.material === "WOOD" && row.size !== "Custom" && gradeOptions && gradeOptions.length > 0;

      const hasApiSpec = row.isClonedOrEdited && row.apiSpecFiles && row.apiSpecFiles.length > 0;
      const hasDefaultSpec = !row.isClonedOrEdited && defaultSpecs && defaultSpecs.length > 0;
      const hasUploadedFiles = row.uploadedFiles && row.uploadedFiles.length > 0;
      const hasSpec = hasApiSpec || hasDefaultSpec || hasUploadedFiles;

      if (!row.material) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('material')} is required.`);
        return;
      }
      if (row.material !== "OTHER" && !row.condition) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('conditionRequired')}`);
        return;
      }
      if (row.material === "WOOD" && !row.palletType) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('palletTypeRequired')}`);
        return;
      }
      if (showMoisture && !row.moisture) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('moistureRequired')}`);
        return;
      }
      if (row.material === "WOOD" && !row.size) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('sizeRequired')}`);
        return;
      }
      if (showGrade && !row.grade) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('gradeRequired')}`);
        return;
      }
      if (row.material === "OTHER" || row.material === "PLASTIC" || row.size === "Custom") {
        if (!row.length || !row.width || !row.height) {
          showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('customLengthWidthHeightRequired')}`);
          return;
        }
      }
      if (!hasSpec) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('uploadSpecificationRequired')}`);
        return;
      }
      if (!row.quantity || Number(row.quantity) <= 0) {
        showValidationError(`${locale('palletRowPrefix')} #${rowNum}: ${locale('quantityGreaterThanZero')}`);
        return;
      }
    }

    setLoader(true);
    const activeShippingAddress = selectedLocationAddress || locationListResponse.find(l => l.value === selectedLocationId)?.response;

    const palletCombinations = [];
    const allSpecFiles = [];

    for (let i = 0; i < palletRows.length; i++) {
      const row = palletRows[i];
      const { defaultSpecs } = getRowCascadingOptions(row);

      let s3Uploaded = [];
      if (row.uploadedFiles && row.uploadedFiles.length > 0) {
        const formData = new FormData();
        row.uploadedFiles.forEach(f => formData.append("file", f));
        const upRes = await dispatch(uploadSpectFile(formData, token));
        if (upRes?.data?.success && upRes?.data?.data?.s3UploadedFiles) {
          s3Uploaded = upRes.data.data.s3UploadedFiles.map(f => ({
            orignalFileName: f.fileName,
            fileName: f.s3FfileName
          }));
        }
      }

      let baseSpecs = [];
      if (row.isClonedOrEdited) {
        baseSpecs = (row.apiSpecFiles || []).map(f => ({
          orignalFileName: f.orignalFileName || f.fileName,
          fileName: f.fileName || f.orignalFileName
        }));
      } else {
        baseSpecs = defaultSpecs.map(f => ({
          orignalFileName: f.orignalFileName || f.label || f.fileName || f.value,
          fileName: f.fileName || f.value
        }));
      }

      const rowSpecs = [...baseSpecs, ...s3Uploaded];
      rowSpecs.forEach(spec => {
        if (!allSpecFiles.some(f => f.fileName === spec.fileName)) {
          allSpecFiles.push(spec);
        }
      });

      const conditionArr = row.material === "OTHER" ? [] : [row.condition];
      const palletDetailObj = {
        material: row.material,
        condition: conditionArr,
        palletType: (row.material === "OTHER" || row.material === "PLASTIC") ? "CUSTOM" : (row.palletType ? row.palletType.toUpperCase() : "CUSTOM"),
        moisture: (row.material === "OTHER" || row.material === "PLASTIC") ? "" : (row.moisture || ""),
        size: row.size || "",
        grade: row.grade || "",
        length: parseFloat(row.length) || "",
        width: parseFloat(row.width) || "",
        height: parseFloat(row.height) || "",
        heatTreared: row.isHeatTreated || false
      };

      if (row.condition === "NEW" && row.newConditionType) {
        palletDetailObj.newConditionType = (row.newConditionType === "New - Hardwood" || row.newConditionType === "HARDWOOD") ? "HARDWOOD" : "SOFTWOOD";
      }

      const { specQty } = getRowCascadingOptions(row);
      const rowMaxQty = specQty || row.maxQuantity || (row.material === "PLASTIC" ? 786 : 540);

      palletCombinations.push({
        palletDetails: palletDetailObj,
        quantity: parseInt(row.quantity),
        originalQuantity: parseInt(rowMaxQty),
        specificationFiles: rowSpecs
      });
    }

    const totalQuantity = palletCombinations.reduce((acc, curr) => acc + (curr.quantity || 0), 0);
    const totalOriginalQuantity = palletCombinations.reduce((acc, curr) => acc + (curr.originalQuantity || curr.quantity || 0), 0);
    const totalLots = palletRows.reduce((acc, curr) => acc + parseInt(curr.lots || 1), 0);

    const payload = {
      palletDetails: null,
      palletCombinations: palletCombinations,
      productImage: null,
      orderRefNumber: isModifyForm ? modifiedOrderRefNo : "",
      equipmentRequirements: equipmentRequirements,
      loadDetails: [{
        requestDeliveryDate: moment(requestDeliveryDate).format('YYYY-MM-DD HH:mm'),
        deliveryFromtime: apptFromStr,
        deliveryTotime: apptToStr,
        lotType: "MULTIPLE",
        poNumber: activePoNumber,
        postType: "NEED",
        quantityDetails: {
          lots: totalLots || 1,
          quantity: totalQuantity,
          originalQuantity: totalOriginalQuantity
        }
      }],
      location: {
        locationId: selectedLocationId || "",
        shippingAddress: activeShippingAddress
      },
      postRecurring: false,
      recurrance: null,
      remark: activeRemark
    };

    if (!isModifyForm) {
      payload.customer = {
        customerId: loggedInUserInfo?.customerId,
        locationType: loggedInUserInfo?.locationType
      };
    } else {
      payload.modifiedBy = loggedInUserInfo?.name;
    }

    try {
      const response = isModifyForm
        ? await dispatch(updateOrder(payload, token))
        : await dispatch(createOrder(payload, token));

      if (response?.data?.success) {
        setModalTitle("");
        setModalContent(isModifyForm ? "Your order request has been updated!" : "Thank you! Your order request has been sent to a PalletTrader+ team member.");
        setModalLink("/manage-orders");
        toggleModal();
      } else {
        showValidationError(response?.data?.error?.message || response?.message || locale('genericErrorLater'));
      }
    } catch (err) {
      showValidationError(locale('genericErrorLater'));
    } finally {
      setLoader(false);
    }
  };

  const currentOrderId = formikRef.current?.values?.orderId !== undefined ? formikRef.current.values.orderId : (clonedOrderRefNo || "");
  const currentPo = formikRef.current?.values?.poNumber || poNumber || "";
  const currentRemark = formikRef.current?.values?.remark || remark || "";

  const formikValues = {
    cloneOrder: isCloningOrder,
    orderId: currentOrderId,
    poNumber: currentPo,
    remark: currentRemark,
    ...palletRows.reduce((acc, row, idx) => {
      acc[`quantity_${idx}`] = row.quantity !== undefined && row.quantity !== null ? row.quantity : "";
      acc[`length_${idx}`] = row.length || "";
      acc[`width_${idx}`] = row.width || "";
      acc[`height_${idx}`] = row.height || "";
      acc[`isHeatTreated_${idx}`] = Boolean(row.isHeatTreated);
      return acc;
    }, {})
  };

  return (
    <Formik innerRef={formikRef} enableReinitialize initialValues={formikValues} onSubmit={() => { }}>
      {({ setFieldValue, values }) => (
        <Form className="form-wrapper">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/png,image/jpeg,application/pdf,.doc,.docx"
            onChange={handleFileChange}
          />

          <Container>
            <Wrapper>
              <FormWrapper>
                <div>
                  <InputFieldsWrapper>
                    <div>
                      {/* Location Title */}
                      <div className="label-heading mb-10" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ margin: 0 }}>{locale('deliveryLocation')}*</p>
                      </div>

                      {/* Clone Order Section */}
                      {!isModifyForm && (
                        <CloneOuterWrapper>
                          <div className="clone-order-wrapper">
                            <CheckboxInput
                              name="cloneOrder"
                              label={locale('clonePreviousOrder')}
                              onChange={(val) => setIsCloningOrder(val)}
                            />
                          </div>

                          {isCloningOrder && (
                            <div style={{ width: '260px' }}>
                              <TextInput
                                name="orderId"
                                maxLength={50}
                                placeholder={locale('enterReferenceId')}
                                blurHandler={(val) => {
                                  if (val) loadOrderDetailsAPI(val, setFieldValue);
                                }}
                              />
                            </div>
                          )}
                        </CloneOuterWrapper>
                      )}

                      {/* Header Row: Location, Equipment, Dates, Appt, PO */}
                      <MixHeaderGrid>
                        <div>
                          <label>{locale('deliveryLocation')}*</label>
                          <Select
                            name="businessName"
                            data={locationListResponse}
                            searchable={true}
                            placeholder={locale('locationName')}
                            displayFloatingLabel={false}
                            selectedValue={selectedLocationId}
                            onChangeHandler={(val, data) => handleLocationChange(data?.value || val)}
                          />
                        </div>

                        <div>
                          <label className="mix-field-label">{locale('equipmentRequirementsMixPallet')}*</label>
                          <MultiSelectCheckboxDropdown
                            options={equipmentList.map((eq, i) => ({ label: eq, value: `equipment${i}` }))}
                            selectedValues={selectedEquipment}
                            placeholder={locale('equipmentRequirementsMixPallet')}
                            onChange={(vals) => {
                              setSelectedEquipment(vals);
                              setEquipmentState({
                                equipment0: vals.includes("equipment0"),
                                equipment1: vals.includes("equipment1"),
                                equipment2: vals.includes("equipment2")
                              });
                            }}
                          />
                        </div>

                        <div>
                          <label>{locale('deliveryDate')}*</label>
                          <DatePicker
                            name="requestDeliveryDate"
                            placeholderText=""
                            type="createOrder"
                            selectedDate={requestDeliveryDate}
                            callBackFun={(dateStr) => setRequestDeliveryDate(dateStr)}
                          />
                        </div>

                        <div>
                          <label>{locale('appointmentWindow')} ({locale('from')})*</label>
                          <TimePickerInput
                            name="apptFrom"
                            fromValue={apptFrom}
                            selectedTimeValue={apptFrom}
                            onChangeHandler={(val) => setApptFrom(val)}
                          />
                        </div>

                        <div>
                          <label>{locale('to')}</label>
                          <TimePickerInput
                            name="apptTo"
                            endTime="23:00"
                            toValue={apptTo}
                            selectedTimeValue={apptTo}
                            onChangeHandler={(val) => setApptTo(val)}
                          />
                        </div>

                        <div>
                          <label>{locale('poNumber')}</label>
                          <TextInput
                            name="poNumber"
                            placeholder={locale('poNumber')}
                            displayFloatingLabel={false}
                          />
                        </div>
                      </MixHeaderGrid>

                      {/* Pallet Details Section */}
                      <div className="label-heading" style={{ marginTop: '20px', marginBottom: '15px' }}>
                        <p>{locale('palletDetails')}<span className="star">*</span></p>
                      </div>

                      {palletRows.map((row, index) => {
                        const { moistureOptions, sizeOptions, gradeOptions, defaultSpecs } = getRowCascadingOptions(row);
                        const showMoisture = row.material === "WOOD" && moistureOptions && moistureOptions.length > 0;
                        const showGrade = row.material === "WOOD" && row.size !== "Custom" && gradeOptions && gradeOptions.length > 0;
                        const isCustom = row.palletType === "custom" || row.material === "OTHER" || row.material === "PLASTIC" || row.size === "Custom";

                        return (
                          <PalletRowContainer key={row.id}>
                            <PalletRowGrid className={isCustom ? (showMoisture ? "has-custom-size-and-moisture" : "has-custom-size") : ""}>
                              {/* 1. Material */}
                              <div>
                                <label className="mix-field-label">{locale("material")}</label>
                                <Select
                                  name={`material_${index}`}
                                  data={materialOptions}
                                  placeholder={locale("material")}
                                  displayFloatingLabel={false}
                                  selectedValue={row.material}
                                  onChangeHandler={(val) => updateRowField(index, "material", val.value || val)}
                                />
                              </div>

                              {/* 2. Condition (for WOOD and PLASTIC) */}
                              {row.material !== "OTHER" && (
                                <div>
                                  <label className="mix-field-label">{locale("condition")}</label>
                                  <ConditionDropdown
                                    material={row.material}
                                    condition={row.condition}
                                    newConditionType={row.newConditionType}
                                    placeholder={locale("condition")}
                                    disabled={!row.material}
                                    onChange={({ condition, newConditionType }) => {
                                      updateRowField(index, "condition", condition);
                                      if (newConditionType !== undefined) {
                                        setPalletRows(prev => {
                                          const updated = [...prev];
                                          updated[index].newConditionType = newConditionType;
                                          return updated;
                                        });
                                      }
                                    }}
                                  />
                                </div>
                              )}

                              {/* 3. Pallet Type (for WOOD) */}
                              {row.material === "WOOD" && (
                                <div>
                                  <label className="mix-field-label">{locale("palletType")}*</label>
                                  <Select
                                    name={`palletType_${index}`}
                                    data={row.condition === "COMBO" ? comboPalletList : palletList}
                                    placeholder={locale("palletType")}
                                    displayFloatingLabel={false}
                                    disabled={!row.condition}
                                    selectedValue={row.palletType}
                                    onChangeHandler={(val) => updateRowField(index, "palletType", val.value || val)}
                                  />
                                </div>
                              )}

                              {/* 4. Moisture */}
                              {showMoisture && (
                                <div>
                                  <label className="mix-field-label">{locale("moisture")}</label>
                                  <Select
                                    name={`moisture_${index}`}
                                    data={moistureOptions}
                                    placeholder={locale("moisture")}
                                    displayFloatingLabel={false}
                                    selectedValue={row.moisture}
                                    onChangeHandler={(val) => updateRowField(index, "moisture", val.value || val)}
                                  />
                                </div>
                              )}

                              {/* 5. Size (for WOOD) */}
                              {row.material === "WOOD" && (
                                <div>
                                  <label className="mix-field-label">{locale("size")}*</label>
                                  <Select
                                    name={`size_${index}`}
                                    data={sizeOptions}
                                    placeholder={locale("size")}
                                    displayFloatingLabel={false}
                                    disabled={!row.palletType && row.material === "WOOD"}
                                    selectedValue={row.size}
                                    onChangeHandler={(val) => updateRowField(index, "size", val.value || val)}
                                  />
                                </div>
                              )}

                              {/* 6. Grade */}
                              {showGrade && (
                                <div>
                                  <label className="mix-field-label">{locale("grade")}</label>
                                  <Select
                                    name={`grade_${index}`}
                                    data={gradeOptions}
                                    placeholder={locale("grade")}
                                    displayFloatingLabel={false}
                                    selectedValue={row.grade}
                                    onChangeHandler={(val) => updateRowField(index, "grade", val.value || val)}
                                  />
                                </div>
                              )}

                              {/* Custom Dimensions */}
                              {isCustom && (
                                <>
                                  <div>
                                    <label className="mix-field-label">{locale("length")}</label>
                                    <NumberInput
                                      name={`length_${index}`}
                                      placeholder={locale("length")}
                                      displayFloatingLabel={false}
                                      maxLength={5}
                                      value={row.length}
                                      onChangeHandler={(val) => updateRowField(index, "length", val)}
                                    />
                                  </div>
                                  <div>
                                    <label className="mix-field-label">{locale("width")}</label>
                                    <NumberInput
                                      name={`width_${index}`}
                                      placeholder={locale("width")}
                                      displayFloatingLabel={false}
                                      maxLength={5}
                                      value={row.width}
                                      onChangeHandler={(val) => updateRowField(index, "width", val)}
                                    />
                                  </div>
                                  <div>
                                    <label className="mix-field-label">{locale("height")}</label>
                                    <NumberInput
                                      name={`height_${index}`}
                                      placeholder={locale("height")}
                                      displayFloatingLabel={false}
                                      maxLength={5}
                                      value={row.height}
                                      onChangeHandler={(val) => updateRowField(index, "height", val)}
                                    />
                                  </div>
                                </>
                              )}

                              {/* 7. Quantity */}
                              <div>
                                <label className="mix-field-label">{locale("quantity")}</label>
                                <NumberInput
                                  name={`quantity_${index}`}
                                  placeholder={locale("quantity")}
                                  displayFloatingLabel={false}
                                  value={row.quantity}
                                  maxLength={4}
                                  decimalScale={0}
                                  maxQuantity={row.maxQuantity || (row.material === "PLASTIC" ? 786 : 540)}
                                  onChangeHandler={(val) => {
                                    const maxAllowed = Number(row.maxQuantity || (row.material === "PLASTIC" ? 786 : 540));
                                    const num = Number(val);
                                    if (num && num > maxAllowed) {
                                      updateRowField(index, "quantity", maxAllowed);
                                    } else {
                                      updateRowField(index, "quantity", val);
                                    }
                                  }}
                                />
                              </div>

                              {/* Action Buttons */}
                              <RowActionGroup>
                                <RowActionButton
                                  type="button"
                                  className="add-btn"
                                  title="Add Pallet Row"
                                  onClick={handleAddRow}
                                >
                                  +
                                </RowActionButton>

                                {palletRows.length > 1 && (
                                  <RowActionButton
                                    type="button"
                                    className="remove-btn"
                                    title="Remove Row"
                                    onClick={() => handleRemoveRow(index)}
                                  >
                                    -
                                  </RowActionButton>
                                )}

                                <RowActionButton
                                  type="button"
                                  className="upload-btn"
                                  title="Upload Spec File"
                                  onClick={() => triggerRowFileUpload(index)}
                                >
                                  <UploadSpecIcon width={16} height={16} />
                                </RowActionButton>
                              </RowActionGroup>
                            </PalletRowGrid>

                            {/* Badges & Uploaded Files */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', gap: '15px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                {row.material === "WOOD" && (
                                  <CheckboxInput
                                    name={`isHeatTreated_${index}`}
                                    label={locale("heatTreated")}
                                    checked={row.isHeatTreated}
                                    onChange={(val) => updateRowField(index, "isHeatTreated", val)}
                                  />
                                )}

                                {row.isClonedOrEdited ? (
                                  row.apiSpecFiles && row.apiSpecFiles.length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Spec File:</span>
                                      {row.apiSpecFiles.map((item, aIdx) => (
                                        <RowUploadFileBadge key={aIdx} style={{ background: '#e0f2fe', borderColor: '#bae6fd' }}>
                                          <img src={UploadDummy} alt="spec" style={{ width: '14px', marginRight: '6px' }} />
                                          <span className="full-filename" style={{ color: '#0284c7', fontWeight: 600 }}>
                                            {item.orignalFileName || item.fileName}
                                          </span>
                                          <span className="remove-icon" onClick={() => handleRemoveApiSpecFile(index, aIdx)}>×</span>
                                        </RowUploadFileBadge>
                                      ))}
                                    </div>
                                  )
                                ) : (
                                  defaultSpecs && defaultSpecs.length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Default Spec:</span>
                                      {defaultSpecs.map((item, dIdx) => (
                                        <RowUploadFileBadge key={dIdx} style={{ background: '#e0f2fe', borderColor: '#bae6fd' }}>
                                          <img src={UploadDummy} alt="spec" style={{ width: '14px', marginRight: '6px' }} />
                                          <span className="full-filename" style={{ color: '#0284c7', fontWeight: 600 }}>
                                            {item?.value}
                                          </span>
                                        </RowUploadFileBadge>
                                      ))}
                                    </div>
                                  )
                                )}
                              </div>

                              <div style={{ textAlign: 'right' }}>
                                {row.uploadedFiles && row.uploadedFiles.length > 0 && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    {row.uploadedFiles.map((file, fIdx) => (
                                      <RowUploadFileBadge key={fIdx}>
                                        <TooltipMaterial placement="top" arrow title={file.name}>
                                          <span className="filename">{file.name}</span>
                                        </TooltipMaterial>
                                        <span className="remove-icon" onClick={() => handleRemoveRowFile(index, fIdx)}>×</span>
                                      </RowUploadFileBadge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </PalletRowContainer>
                        );
                      })}

                      {/* Remarks */}
                      <div className="label-heading" style={{ marginTop: '25px' }}>
                        <p>{locale("remarks")}</p>
                      </div>
                      <div className="col-12 pl-0">
                        <TextArea
                          name="remark"
                          maxLength="800"
                          placeholder={locale("remarks")}
                          displayFloatingLabel={false}
                        />
                      </div>
                    </div>
                  </InputFieldsWrapper>
                </div>

                <Footer style={{ textAlign: 'center', marginTop: '30px' }}>
                  <Button
                    type="app-green-button"
                    handleClick={() => handleMixSubmit(values)}
                    style={{ width: '240px', margin: '0 auto' }}
                  >
                    {isModifyForm ? locale("updateRequest") : locale("submitMixRequest")}
                  </Button>
                </Footer>
              </FormWrapper>
            </Wrapper>
          </Container>

          <AlertModal
            isOpen={modal}
            toggle={toggleModal}
            title={modalTitle}
            content={modalContent}
            link={modalLink}
            className="alert-msg-container"
            InnerClassName="alert-msg"
            callBackAlert={() => {
              if (modalLink) navigate(modalLink);
              else toggleModal();
            }}
          />
        </Form>
      )}
    </Formik>
  );
};

export default MixPalletRequestOrder;
